import { useEffect } from 'react'
import { ErrorBoundary as Component, FallbackProps } from 'react-error-boundary'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'

// Keep in sync with the inline <script> in index.html (key, cooldown, error patterns).
const RELOAD_KEY = '_chunk_reload'
const RELOAD_WINDOW_MS = 10_000

function isChunkLoadError(error: Error): boolean {
  return (
    error.message?.includes('Failed to fetch dynamically imported module') ||
    error.message?.includes('Loading chunk') ||
    error.message?.includes('is not a valid JavaScript MIME type') ||
    error.name === 'ChunkLoadError'
  )
}

/**
 * Guard against infinite reload loops: only auto-reload once within
 * RELOAD_WINDOW_MS. If the page was already reloaded recently, show
 * the manual fallback instead.
 */
function shouldAutoReload(): boolean {
  try {
    const last = sessionStorage.getItem(RELOAD_KEY)
    if (last && Date.now() - Number(last) < RELOAD_WINDOW_MS) return false
    return true
  } catch {
    return false
  }
}

function ChunkErrorFallback() {
  useEffect(() => {
    if (shouldAutoReload()) {
      sessionStorage.setItem(RELOAD_KEY, Date.now().toString())
      window.location.reload()
    }
  }, [])

  return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Typography variant='h6' gutterBottom>
        Page failed to load
      </Typography>
      <Typography color='text.secondary' sx={{ mb: 3 }}>
        This may be due to a network issue or a new version deployment.
      </Typography>
      <Button
        variant='contained'
        onClick={() => {
          try { sessionStorage.removeItem(RELOAD_KEY) } catch {}
          window.location.reload()
        }}>
        Refresh Page
      </Button>
    </Box>
  )
}

function Fallback({ error }: FallbackProps) {
  if (isChunkLoadError(error)) {
    return <ChunkErrorFallback />
  }

  return (
    <Alert severity='error'>
      <AlertTitle sx={{ fontWeight: 600 }}>
        Sorry, something went wrong
      </AlertTitle>
      {error.message}
    </Alert>
  )
}

export default function ErrorBoundary(props: React.PropsWithChildren) {
  return <Component FallbackComponent={Fallback}>{props.children}</Component>
}
